
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';
import { Association } from '../../../common/models/association';
import { CurrentUser } from '../../../common/models/user';
import { AssociationService } from '../../../common/services/association.service';
import { StorageService } from '../../../common/services/storage.service';
import { SearchService } from '../../../common/services/search.service';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { GenericDialogComponent } from '../../../common/components/dialog/dialog';
import { Subject, takeUntil } from 'rxjs';


@Component({
    selector: 'app-associations',
    imports: [MatCardModule, MatMenuModule, MatButtonModule, MatTableModule, MatPaginatorModule, MatTooltipModule, MatIconModule, MatDialogModule, MatSnackBarModule],
    templateUrl: './associations.component.html',
    styleUrl: './associations.component.scss'
})
export class AssociationsComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();

    associations: Association[] = [];
    filteredAssociations: Association[] = [];
    user: CurrentUser | null = null;

  
    isToggled = false;

    private readonly snackbarConfig: MatSnackBarConfig = {
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top'
    };
  
  
    constructor(
      private associationService: AssociationService,
      private storageService: StorageService,
      private searchService: SearchService,
      public themeService: CustomizerSettingsService,
      private dialog: MatDialog,
      private router: Router,
      private snackBar: MatSnackBar
    ) {}
  
    ngOnInit() {
      this.user = this.storageService.getUser();
      this.themeService.isToggled$.pipe(takeUntil(this.destroy$)).subscribe(isToggled => {
        this.isToggled = isToggled;
      });
      this.searchService.currentSearchTerm$.pipe(takeUntil(this.destroy$)).subscribe(term => {
        this.applySearch(term);
      });
   
      this.fetchAssociations();
    }

    ngOnDestroy() {
      this.destroy$.next();
      this.destroy$.complete();
    }
  
    fetchAssociations(): void {
      const municipalityId = this.user?.municipalityId ?? ''; 
      this.associationService.getAssociations(municipalityId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (data) => {
          this.associations = data;
          this.filteredAssociations = data;
        },
        error: (error) => {
          console.error('Error fetching associations', error);
        }
      });
    }

    applySearch(searchTerm: string): void {
      searchTerm = searchTerm.trim().toLowerCase();
      if (searchTerm) {
        this.filteredAssociations = this.associations.filter((association: Association) => {
          return (
            association.name.toLowerCase().includes(searchTerm) ||
            association.oib.toLowerCase().includes(searchTerm) ||
            association.email.toLowerCase().includes(searchTerm)
          );
        });
      } else {
        this.filteredAssociations = [...this.associations]; // Reset to original list if no search term
      }
    }

    confirmDelete(association: Association): void {
      const dialogRef = this.dialog.open(GenericDialogComponent, {
        width: '50%',
        data: {
          title: 'Potvrdite brisanje',
          message: `Jeste li sigurni da želite obrisati udrugu "${association.name}"?`,
          confirmButtonText: 'Da',
          cancelButtonText: 'Ne',
        },
      });
  
      dialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
        if (result === true) {
          this.deleteAssociation(association.id);
        }
      });
    }
  
    deleteAssociation(associationId: string): void {
      this.associationService.deleteAssociation(associationId).pipe(takeUntil(this.destroy$)).subscribe(
        () => {
          this.fetchAssociations(); // Refresh the list after deletion
          this.snackBar.open('Udruga je obrisana', 'Zatvori', this.snackbarConfig);
        },
        (error) => {
          console.error('Error deleting association', error);
        }
      );
    }
    

    goToToCreateAssociation(): void {
      this.router.navigate(['municipality', 'associations', 'create']);
  }

    goToToEditAssociation(association: Association): void {
      this.router.navigate(['municipality', 'associations', association.id, 'edit']);
    }

    goToViewAssociation(association: Association): void {
      this.router.navigate(['municipality', 'associations', association.id, 'view']);
    }
  }
